import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Grid, TextField, CircularProgress } from '@mui/material';
import { createPortal } from 'react-dom';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import SearchIcon from '@mui/icons-material/Search';
import PurchaseService from 'services/PurchaseService';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { PRINT_TYPES } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import { ShopContext } from 'Context/ShopContext';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { AlertMessagesContext } from 'react-alert-messages';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { saveAs } from 'file-saver';
import CsvHelper from 'helpers/CsvHelper';
import SyncIcon from '@mui/icons-material/Sync';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import PrintIcon from '@mui/icons-material/Print';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useTranslation } from 'react-i18next';

function PurchaseTaxSummary() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [filterFromDate, setFilterFromDate] = useState(new Date());
  const [filterToDate, setFilterToDate] = useState(new Date());

  const [searchText, setSearchText] = useState('');
  const [purchases, setPurchase] = useState('');
  const [filteredPurchases, setFilteredPurchases] = useState([]);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
  });
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    id: tCommon('ID'),
    invoice_no: tCommon('Invoice No'),
    bill_date: tCommon('Bill Date'),
    date: tCommon('Date'),
    vendor: {
      title: tCommon('Vendor Name'),
      getValue: (purchaseItem) => purchaseItem?.vendor?.name ?? purchaseItem?.vendor_name_old ?? '-',
    },
    plu: {
      title: tCommon('PLU'),
      getValue: (purchaseItem) => purchaseItem?.item?.plu,
    },
    name: {
      title: tCommon('Item Name'),
      getValue: (purchaseItem) => purchaseItem?.item?.name,
    },
    rate: {
      title: tCommon('Cost'),
      getValue: (purchaseItem) => purchaseItem?.item?.rate,
    },
    quantity: {
      title: tCommon('Quantity'),
      getValue: (purchaseItem) => purchaseItem?.item?.quantity,
    },
    tax: {
      title: tCommon('Tax'),
      getValue: (purchaseItem) => ShopsHelper.getAmountFormatted(shop, purchaseItem?.item?.tax || 0),
    },
    total: {
      title: tCommon('Total'),
      getValue: (purchaseItem) => {
        const amount = purchaseItem?.item?.rate * purchaseItem?.item?.quantity + (purchaseItem?.item?.tax || 0);
        return ShopsHelper.getAmountFormatted(shop, amount);
      },
    },
    total_amount: {
      title: tCommon('Purchase Total'),
      getValue: (purchaseItem) => ShopsHelper.getAmountFormatted(shop, purchaseItem?.total_amount || 0),
    },
  };

  const toCsvBtnPressed = async () => {
    const csvFormattedPurchases = purchases.flatMap((obj) =>
      obj.items.map((item, index) => ({
        id: obj.id,
        invoice_no: obj.invoice_no,
        bill_date: obj.bill_date,
        date: obj.date,
        vendor: obj.vendor,
        vendor_name_old: obj.vendor_name_old,
        total_amount: index === 0 ? obj.total_amount : undefined,
        item: {
          ...item,
          name: item.item.name,
        },
      }))
    );

    const csv = CsvHelper.getString(csvFormattedPurchases, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `PURCHASE-TAX-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const headerData = [
    {
      label: tCommon('Vendor'),
      id: 'vendor',
      type: 'callback',
      viewRender: (item) => item?.vendor?.name ?? item?.vendor_name_old ?? '-',
    },
    {
      label: tCommon('Purchase Date'),
      id: 'date',
      type: 'text',
    },
    {
      label: tCommon('Bill Date'),
      id: 'bill_date',
      type: 'text',
    },
    {
      label: tCommon('Invoice No'),
      id: 'invoice_no',
      type: 'text',
    },
    {
      label: tCommon('Total Tax'),
      id: 'tax',
      type: 'callback',
      viewRender: (item) => {
        let totalTax = (item?.items || []).reduce((acc, i) => acc + (i.tax || 0), 0);
        return totalTax;
      },
    },
    {
      label: tCommon('Total Amount'),
      id: 'total_amount',
      type: 'floatAmount',
    },
  ];

  const _getLoadPurchaseRequestParams = useCallback(() => {
    return {
      date_start: DateTimeHelpers.getDateYMD(filterFromDate),
      date_end: DateTimeHelpers.getDateYMD(filterToDate),
    };
  }, [filterFromDate, filterToDate]);

  const generatePurchaseHistory = () => {
    const { startDate, endDate } = dynamicDateRange('PURCHASE_TAX_SUMMARY');

    setFilterFromDate(startDate);
    setFilterToDate(endDate);

    getPurchase({
      date_start: DateTimeHelpers.getDateYMD(startDate),
      date_end: DateTimeHelpers.getDateYMD(endDate),
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: startDate,
      filterToDate: endDate,
    });
  };

  const getPurchase = useCallback(
    async (customParams) => {
      setLoadingIndicator(true);
      try {
        const params = customParams || _getLoadPurchaseRequestParams();

        const res = await PurchaseService.getPurchaseDetails(params);
        setPurchase(res);
        setFilteredPurchases(res);
      } catch (err) {
        postAlertMessage({ text: err.message, type: 'failed' });
      }
      setLoadingIndicator(false);
    },
    [_getLoadPurchaseRequestParams, postAlertMessage]
  );

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return filterFromDate !== prevFilterData.filterFromDate || filterToDate !== prevFilterData.filterToDate;
  }, [filterFromDate, filterToDate, prevFilterData.filterFromDate, prevFilterData.filterToDate]);

  useEffect(() => {
    generatePurchaseHistory();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(
      tReport('Purchase Tax Summary Report'),
      filterFromDate,
      filterToDate,
      headerData,
      filteredPurchases,
      {
        [tCommon('Total Tax')]: `${shop?.currency || ''} ${summary.totalTax}`,
        [tCommon('Total Amount')]: `${shop?.currency || ''} ${summary.totalAmount}`,
      }
    );
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setFilteredPurchases((purchases || []).filter((data) => data.name.toLowerCase().includes(value)));
  };

  const _getPurchasesTaxReportSummart = (purchaseData) => {
    let totalAmount = 0;
    let totalTax = 0;
    (purchaseData || []).forEach((data) => {
      totalAmount += data?.total_amount;
      totalTax += (data.items || []).reduce((acc, dt) => acc + (dt?.tax || 0), 0);
    });
    return {
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
    };
  };

  const summary = _getPurchasesTaxReportSummart(filteredPurchases);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a', marginRight: 5 }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            Csv
          </Button>
        </div>
      </div>

      <div className={styles.changeable}>
        <Grid container spacing={1} alignItems="flex-end">
          <Grid item xs={12}>
            <h2 className={styles.subTitle}>
              {tReport('Purchase Tax Summary Report for {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={tCommon('Search purchase')}
              value={searchText}
              onChange={(e) => {
                handleSearch(e.target.value);
              }}
              InputProps={{
                endAdornment: <SearchIcon />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="PURCHASE_TAX_SUMMARY"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              style={{
                backgroundColor: '#00a65a',
                color: '#fff',
                height: 40,
                minWidth: 160,
              }}
              onClick={generatePurchaseHistory}
              disabled={isShowGenerateButton === false || loadingIndicator}
              startIcon={loadingIndicator ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            >
              {tCommon('Generate')}
            </Button>
          </Grid>
        </Grid>
      </div>
      {isShowGenerateButton === null ? (
        <Info
          title={tReport('Set filters and click generate button')}
          content={tReport(
            'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
          )}
        />
      ) : (
        purchases &&
        (filteredPurchases && filteredPurchases.length ? (
          <div className={styles.tableContainer}>
            <Grayout open={isShowGenerateButton} />
            <DataTable
              columns={headerData}
              rows={filteredPurchases}
              summary={{
                vendor: tCommon('Total'),
                total_amount: `${shop?.currency} ${summary.totalAmount}`,
                tax: `${shop?.currency} ${summary.totalTax}`,
              }}
            />
          </div>
        ) : (
          !loadingIndicator && (
            <Info
              title={tReport('You have no purchases to list')}
              content={tReport("You haven't any purchase to list with given properties")}
            />
          )
        ))
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(PurchaseTaxSummary);
